import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase'
import { useQueryLog } from './useQueryLog'

export function useDashboard(startDate, endDate) {
  const { logQuery } = useQueryLog()
  const [metrics, setMetrics] = useState({
    totalClients: 0,
    activePolicies: 0,
    inactivePolicies: 0,
    advisorCommissions: [],
    thisMonthTotal: 0,
    ytdTotal: 0,
    topClientsByPolicies: [],
    monthlyTrend: [],
    thisMonthLabel: '',
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchMetrics = useCallback(async () => {
    if (!startDate || !endDate) return
    try {
      setLoading(true)

      // "This month" = calendar month of endDate
      const endD = new Date(endDate)
      const thisMonth = `${endD.getFullYear()}-${String(endD.getMonth() + 1).padStart(2, '0')}`

      // Trend: last 6 months ending at endDate
      const trendStartDate = new Date(endD)
      trendStartDate.setMonth(trendStartDate.getMonth() - 5)
      trendStartDate.setDate(1)
      const trendStart = trendStartDate.toISOString().slice(0, 10)

      // Fetch from earliest of startDate or 6-month trend start
      const queryFrom = startDate < trendStart ? startDate : trendStart

      logQuery('SELECT', 'clients')
      logQuery('SELECT', 'policies')
      logQuery('SELECT', 'policy_transactions')

      const [clientRes, activePoliciesRes, totalPoliciesRes, policyClientsRes, trendRes] = await Promise.all([
        supabase.from('clients').select('id', { count: 'exact', head: true }),
        supabase.from('policies').select('id', { count: 'exact', head: true }).eq('active', true),
        supabase.from('policies').select('id', { count: 'exact', head: true }),
        supabase.from('policies').select('client_id').not('client_id', 'is', null),
        supabase
          .from('policy_transactions')
          .select('transaction_date, advisor_share_excl, policy_invoice_no')
          .eq('active', true)
          .gte('transaction_date', queryFrom)
          .lte('transaction_date', endDate),
      ])

      // Top clients by policy count
      const clientPolicyMap = {}
      ;(policyClientsRes.data || []).forEach(p => {
        if (p.client_id) clientPolicyMap[p.client_id] = (clientPolicyMap[p.client_id] || 0) + 1
      })
      const topClientIds = Object.entries(clientPolicyMap)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([id]) => id)

      let topClientsByPolicies = []
      if (topClientIds.length > 0) {
        logQuery('SELECT', 'clients')
        const { data: topClientsData } = await supabase
          .from('clients')
          .select('id, full_name, customer_code, client_status')
          .in('id', topClientIds)
        topClientsByPolicies = topClientIds
          .map(id => {
            const c = topClientsData?.find(c => c.id === id)
            return c ? { ...c, policyCount: clientPolicyMap[id] } : null
          })
          .filter(Boolean)
      }

      // Build monthly map from transaction_date
      const monthMap = {}
      let ytdTotal = 0
      ;(trendRes.data || []).forEach(tx => {
        const m = tx.transaction_date?.slice(0, 7)
        if (!m) return
        const amt = Number(tx.advisor_share_excl) || 0
        monthMap[m] = (monthMap[m] || 0) + amt
        // YTD = within the user-selected date range
        if (tx.transaction_date >= startDate && tx.transaction_date <= endDate) {
          ytdTotal += amt
        }
      })

      const thisMonthTotal = monthMap[thisMonth] || 0

      // 6-month trend ending at endDate
      const monthlyTrend = []
      for (let i = 5; i >= 0; i--) {
        const d = new Date(endD)
        d.setMonth(d.getMonth() - i)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        monthlyTrend.push({
          month: d.toLocaleDateString('en-ZA', { month: 'short' }),
          amount: Math.round((monthMap[key] || 0) * 100) / 100,
        })
      }

      // Top 5 policies by commission for thisMonth
      const advMap = {}
      ;(trendRes.data || [])
        .filter(tx => tx.transaction_date?.startsWith(thisMonth))
        .forEach(tx => {
          const key = tx.policy_invoice_no || 'Unassigned'
          advMap[key] = (advMap[key] || 0) + (Number(tx.advisor_share_excl) || 0)
        })
      const advisorCommissions = Object.entries(advMap)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([advisor, amount]) => ({ advisor, amount }))

      const thisMonthLabel = new Date(thisMonth + '-01').toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })

      setMetrics({
        totalClients: clientRes.count || 0,
        activePolicies: activePoliciesRes.count || 0,
        inactivePolicies: (totalPoliciesRes.count || 0) - (activePoliciesRes.count || 0),
        advisorCommissions,
        thisMonthTotal,
        ytdTotal,
        topClientsByPolicies,
        monthlyTrend,
        thisMonthLabel,
      })
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  useEffect(() => { fetchMetrics() }, [fetchMetrics])

  return { ...metrics, loading, error }
}
